// macadamia-app/src/components/ImportFolderModal.tsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../../context/WebSocketContext';
import { DocumentImportUpdate } from '../../../hooks/useDocumentImportNotifications';
import {
    CloudArrowUpIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';

interface ImportFolderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onFolderImported: (importedFolder: any) => void;
}

type ImportStep = 'upload' | 'uploading' | 'background' | 'success' | 'error';

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'image/png', 'image/jpeg', 'image/jpg', 'image/webp',
    'image/gif', 'image/bmp', 'image/tiff',
    'text/markdown', 'text/plain',
];

const ImportFolderModal: React.FC<ImportFolderModalProps> = ({
    isOpen,
    onClose,
    onFolderImported
}) => {
    const { socket, isConnected } = useWebSocket();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
    const [dragActive, setDragActive] = useState(false);
    const [fileName, setFileName] = useState<string>('');
    const [fileSize, setFileSize] = useState<number>(0);
    const [fileType, setFileType] = useState<string>('');
    const [progress, setProgress] = useState<DocumentImportUpdate | null>(null);
    const [createdDossier, setCreatedDossier] = useState<{ id: string; title: string } | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const activeJobIdRef = useRef<string | null>(null);

    // Écouter les mises à jour WebSocket (listener permanent, pas de dépendance sur activeJobId)
    useEffect(() => {
        if (!socket || !isConnected) return;

        const handleImportUpdate = (data: DocumentImportUpdate) => {
            if (!activeJobIdRef.current || data.jobId !== activeJobIdRef.current) return;

            setProgress(data);

            if (data.status === 'completed' && data.dossierId) {
                setCreatedDossier({ id: data.dossierId, title: data.dossierTitle || 'Document importé' });
                setCurrentStep('success');
                activeJobIdRef.current = null;
            } else if (data.status === 'failed') {
                setErrorMessage(data.error || 'Erreur inconnue lors du traitement');
                setCurrentStep('error');
                activeJobIdRef.current = null;
            }
        };

        socket.on('documentImportUpdate', handleImportUpdate);

        return () => {
            socket.off('documentImportUpdate', handleImportUpdate);
        };
    }, [socket, isConnected]);

    // Fonctions utilitaires
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (type: string, name: string) => {
        if (type.includes('pdf')) return '';
        if (type.includes('word') || type.includes('document')) return '';
        if (type.includes('image')) return '️';
        if (name.toLowerCase().endsWith('.md') || type.includes('markdown')) return '';
        return '';
    };

    const isFileAllowed = (file: File): boolean => {
        const isMarkdown = file.name.toLowerCase().endsWith('.md');
        return isMarkdown || ALLOWED_MIME_TYPES.includes(file.type);
    };

    // Gestion drag & drop
    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    }, []);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleFiles(e.target.files);
        }
    };

    // Upload du fichier vers le backend (async)
    const handleFiles = async (files: FileList) => {
        if (files.length === 0) return;

        const file = files[0];

        // Validation taille
        if (file.size > MAX_FILE_SIZE) {
            setErrorMessage('Fichier trop volumineux (maximum 200 MB)');
            setCurrentStep('error');
            return;
        }

        // Validation type
        if (!isFileAllowed(file)) {
            setErrorMessage('Type de fichier non supporté. Formats acceptés : PDF, Word (DOC, DOCX), Images (PNG, JPG, WEBP, GIF, BMP, TIFF), Markdown (.md)');
            setCurrentStep('error');
            return;
        }

        setFileName(file.name);
        setFileSize(file.size);
        setFileType(file.type);
        setCurrentStep('uploading');
        setErrorMessage('');

        try {
            const formData = new FormData();
            formData.append('document', file);

            const API_URL = process.env.REACT_APP_URL_API_MACADAMIA;
            const response = await fetch(`${API_URL}/document-import/start`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Erreur HTTP ${response.status}` }));
                throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
            }

            const result = await response.json();
            const jobId = result.data.jobId;

            activeJobIdRef.current = jobId;
            setProgress({
                jobId,
                status: 'pending',
                currentStep: 'Démarrage du traitement...',
                progressPercent: 0,
            });
            setCurrentStep('background');

        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Erreur lors de l\'envoi du fichier');
            setCurrentStep('error');
        }
    };

    // Texte lisible pour l'étape en cours
    const getStepLabel = (step: string): string => {
        switch (step) {
            case 'pending': return 'En attente de traitement...';
            case 'extracting_images': return 'Extraction des images...';
            case 'transcribing': return 'Transcription du document...';
            case 'analyzing': return 'Analyse de la structure...';
            case 'creating_dossier': return 'Création du dossier...';
            default: return step || 'Traitement en cours...';
        }
    };

    // Actions
    const handleViewDossier = () => {
        if (createdDossier) {
            navigate(`/dossiers/${createdDossier.id}`);
            handleClose();
        }
    };

    const handleClose = () => {
        // Si un job est en cours, on ferme la modal mais le job continue en arrière-plan
        setCurrentStep('upload');
        setFileName('');
        setFileSize(0);
        setFileType('');
        activeJobIdRef.current = null;
        setProgress(null);
        setCreatedDossier(null);
        setErrorMessage('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    const handleRetry = () => {
        setCurrentStep('upload');
        setFileName('');
        setFileSize(0);
        setFileType('');
        activeJobIdRef.current = null;
        setProgress(null);
        setCreatedDossier(null);
        setErrorMessage('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-mcdm-third-400 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-mcdm-third-300">
                    <h2 className="text-xl text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi">
                        Importer un document
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-mcdm-third-500 dark:hover:text-mcdm-primary-300"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Upload Step - Zone de dépôt */}
                    {currentStep === 'upload' && (
                        <div>
                            <div
                                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                                    dragActive
                                        ? 'border-mcdm-secondary-400 bg-mcdm-secondary-50 dark:bg-mcdm-secondary-900/20'
                                        : 'border-gray-300 dark:border-mcdm-third-300 hover:border-gray-400'
                                }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff,.md"
                                    onChange={handleFileInput}
                                    className="hidden"
                                />

                                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                <h4 className="text-l font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                    Déposez votre document ici
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    PDF, Word (DOC, DOCX), Images (PNG, JPG, WEBP, GIF, BMP, TIFF), Markdown (.md)
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
                                    Taille maximum : 200 MB
                                </p>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-mcdm-green-400 text-white text-sm px-6 py-2 rounded hover:bg-mcdm-green-500 transition-colors"
                                >
                                    Choisir un fichier
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Uploading Step - Envoi en cours */}
                    {currentStep === 'uploading' && (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mcdm-secondary-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                Envoi du document...
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {fileName} ({formatFileSize(fileSize)})
                            </p>
                        </div>
                    )}

                    {/* Background Step - Traitement en arrière-plan */}
                    {currentStep === 'background' && progress && (
                        <div>
                            {/* File Info */}
                            <div className="bg-gray-50 dark:bg-mcdm-third-300 rounded-lg p-4 mb-6">
                                <div className="flex items-center space-x-3">
                                    <div className="text-2xl">{getFileIcon(fileType, fileName)}</div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                                            {fileName}
                                        </h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {formatFileSize(fileSize)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Barre de progression */}
                            <div className="mb-6">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-lg font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                                        Traitement en cours...
                                    </h3>
                                    <span className="text-sm text-gray-500">
                                        {progress.progressPercent}%
                                    </span>
                                </div>

                                <div className="w-full bg-gray-200 dark:bg-mcdm-third-300 rounded-full h-3 overflow-hidden">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-mcdm-secondary-400 to-mcdm-green-400"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${progress.progressPercent}%` }}
                                        transition={{ duration: 0.5, ease: "easeInOut" }}
                                    />
                                </div>

                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                    {getStepLabel(progress.status)}
                                </p>
                            </div>

                            {/* Message informatif */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                    Le traitement se poursuit en arrière-plan. Vous pouvez fermer cette fenêtre
                                    et continuer à travailler. Vous serez notifié lorsque l'import sera terminé.
                                </p>
                            </div>

                            {/* Bouton fermer */}
                            <div className="flex justify-center">
                                <button
                                    onClick={handleClose}
                                    className="border border-gray-300 dark:border-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 px-6 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-mcdm-third-300 font-medium transition-colors"
                                >
                                    Fermer et continuer à travailler
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Success Step */}
                    {currentStep === 'success' && createdDossier && (
                        <div className="text-center">
                            <div className="mb-6">
                                <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
                                <h3 className="text-xl font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                    Dossier créé avec succès !
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Votre document a été importé et structuré automatiquement.
                                </p>
                            </div>

                            {/* Dossier Info */}
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                                <h4 className="font-medium text-green-800 dark:text-green-200">
                                    {createdDossier.title}
                                </h4>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={handleViewDossier}
                                    className="bg-mcdm-secondary-400 text-white px-6 py-2 rounded-lg hover:bg-mcdm-secondary-500 font-medium transition-colors"
                                >
                                    Voir le dossier
                                </button>
                                <button
                                    onClick={handleClose}
                                    className="border border-gray-300 dark:border-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 px-6 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-mcdm-third-300 font-medium transition-colors"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Error Step */}
                    {currentStep === 'error' && (
                        <div>
                            {errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('balance') ? (
                                <>
                                    <div className="text-center mb-6">
                                        <ExclamationCircleIcon className="mx-auto h-16 w-16 text-amber-500 mb-4" />
                                        <h3 className="text-xl font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                            Tokens insuffisants
                                        </h3>
                                    </div>

                                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                                        <p className="text-sm text-amber-700 dark:text-amber-300">
                                            Vous n'avez pas assez de tokens pour importer ce document.
                                            Rechargez votre solde pour continuer.
                                        </p>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <button
                                            onClick={handleClose}
                                            className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                                        >
                                            Fermer
                                        </button>
                                        <button
                                            onClick={() => { navigate('/mes-tokens'); handleClose(); }}
                                            className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 font-medium transition-colors"
                                        >
                                            Recharger mes tokens
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="text-center mb-6">
                                        <ExclamationCircleIcon className="mx-auto h-16 w-16 text-red-500 mb-4" />
                                        <h3 className="text-xl font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                            Erreur lors de l'import
                                        </h3>
                                    </div>

                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                                        <p className="text-sm text-red-700 dark:text-red-300">
                                            {errorMessage}
                                        </p>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <button
                                            onClick={handleClose}
                                            className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            onClick={handleRetry}
                                            className="bg-mcdm-secondary-400 text-white px-6 py-2 rounded-lg hover:bg-mcdm-secondary-500 font-medium transition-colors"
                                        >
                                            Réessayer
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ImportFolderModal;
